(function () {
  const DEFAULT_DATA_URL = 'https://wpc-wildlife-live.jbmanning5.workers.dev/dashboard.json';
  const HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
  const CATEGORY_ALIASES = {
    bird: ['bird', 'birds', 'aves'],
    'insect-amphibian': ['insect', 'insects', 'amphibian', 'amphibians', 'frog', 'frogs']
  };

  function init(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const state = {
      data: null,
      rows: [],
      filter: 'all',
      search: '',
      expanded: false
    };

    bindControls(root, state);
    load(root, state);
  }

  async function load(root, state) {
    const url = root.dataset.dataUrl || DEFAULT_DATA_URL;
    const status = root.querySelector('[data-fds-status]');

    try {
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`.trim());

      const data = await response.json();
      state.data = normalizeData(data);
      state.rows = state.data.species;
      render(root, state);
    } catch (error) {
      if (status) status.textContent = 'Data unavailable';
      root.querySelector('[data-fds-grid]').innerHTML = `<div class="wpc-full-day-soundscape__error">The full-day soundscape could not be loaded right now. ${escapeHtml(error.message)}</div>`;
    }
  }

  function bindControls(root, state) {
    const search = root.querySelector('[data-fds-search]');
    if (search) {
      search.addEventListener('input', () => {
        state.search = search.value.trim().toLowerCase();
        state.expanded = false;
        render(root, state);
      });
    }

    root.querySelectorAll('[data-fds-filter]').forEach(button => {
      button.addEventListener('click', () => {
        state.filter = button.dataset.fdsFilter || 'all';
        state.expanded = false;
        root.querySelectorAll('[data-fds-filter]').forEach(item => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        render(root, state);
      });
    });

    const scroller = root.querySelector('[data-fds-scroller]');
    if (scroller) {
      scroller.addEventListener('scroll', () => updatePosition(root));
    }

    const desktopQuery = window.matchMedia('(min-width: 621px)');
    const updateForViewport = () => render(root, state);
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', updateForViewport);
    else desktopQuery.addListener(updateForViewport);
  }

  function normalizeData(payload) {
    const soundscape = payload.fullDaySoundscape || null;
    if (soundscape && Array.isArray(soundscape.species)) {
      const date = soundscape.date || payload.date || dateFromTimestamp(payload.updatedAt);
      const timezone = soundscape.timezone || 'America/Chicago';
      const currentHour = currentDataHour(date, payload.updatedAt || soundscape.updatedAt || '', timezone);
      const rollingSpecies = soundscape.species
        .map(normalizeSoundscapeSpecies)
        .filter(Boolean)
        .filter(row => row.totalDetections > 0);
      const species = rollingSpecies
        .map(row => applyTodayWindow(row, currentHour))
        .filter(row => row.totalDetections > 0);

      return {
        date,
        updatedAt: payload.updatedAt || soundscape.updatedAt || '',
        timezone,
        currentHour,
        solar: soundscape.solar || payload.solar || null,
        rollingSpecies,
        species
      };
    }

    const matrix = payload.activityMatrix || {};
    const rows = Array.isArray(matrix.species) ? matrix.species : [];
    const date = matrix.date || payload.date || dateFromTimestamp(payload.updatedAt);
    const timezone = 'America/Chicago';
    const currentHour = currentDataHour(date, payload.updatedAt || '', timezone);
    const rollingSpecies = rows
      .map(normalizeMatrixSpecies)
      .filter(Boolean)
      .filter(row => row.totalDetections > 0);
    const species = rollingSpecies
      .map(row => applyTodayWindow(row, currentHour))
      .filter(row => row.totalDetections > 0);

    return {
      date,
      updatedAt: payload.updatedAt || '',
      timezone,
      currentHour,
      solar: payload.solar || null,
      rollingSpecies,
      species
    };
  }

  function normalizeSoundscapeSpecies(row) {
    const counts = normalizeCounts(row.hourlyCounts);
    if (!counts) return null;

    return {
      commonName: row.commonName || row.common_name || row.species || 'Unknown species',
      scientificName: row.scientificName || row.scientific_name || '',
      imageUrl: row.imageUrl || row.thumbnailDataUri || '',
      category: normalizeCategory(row.category || row.organismType || row.taxonomyClass || ''),
      totalDetections: Number(row.totalDetections || counts.reduce((sum, count) => sum + count, 0)),
      hourlyCounts: counts,
      hourlyDetails: Array.isArray(row.hourlyDetails) ? row.hourlyDetails : []
    };
  }

  function normalizeMatrixSpecies(row) {
    const counts = normalizeCounts(row.hourlyCounts);
    if (!counts) return null;

    return {
      commonName: row.commonName || row.common_name || row.species || 'Unknown species',
      scientificName: row.scientificName || row.scientific_name || '',
      imageUrl: row.imageUrl || row.thumbnailDataUri || '',
      category: normalizeCategory(row.category || row.organismType || row.taxonomyClass || ''),
      totalDetections: Number(row.totalDetections || counts.reduce((sum, count) => sum + count, 0)),
      hourlyCounts: counts,
      hourlyDetails: []
    };
  }

  function normalizeCounts(value) {
    if (!Array.isArray(value)) return null;
    return HOURS.map((_, index) => Math.max(0, Number(value[index] || 0)));
  }

  function applyTodayWindow(row, currentHour) {
    if (!Number.isFinite(currentHour)) return row;
    const counts = row.hourlyCounts.map((count, index) => index <= currentHour ? count : 0);
    const total = counts.reduce((sum, count) => sum + count, 0);
    return {
      ...row,
      totalDetections: total,
      hourlyCounts: counts
    };
  }

  function currentDataHour(date, updatedAt, timezone) {
    if (!date) return null;

    const updated = String(updatedAt || '');
    if (updated.startsWith(`${date}T`)) {
      const hour = Number(updated.slice(11, 13));
      return Number.isFinite(hour) ? Math.max(0, Math.min(23, hour)) : null;
    }

    const now = new Date();
    const today = dateInTimeZone(now, timezone);
    if (date !== today) return null;

    return Number(new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      hour12: false
    }).format(now));
  }

  function dateInTimeZone(value, timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(value).reduce((memo, part) => {
      memo[part.type] = part.value;
      return memo;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  function normalizeCategory(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (CATEGORY_ALIASES.bird.includes(raw)) return 'bird';
    if (CATEGORY_ALIASES['insect-amphibian'].includes(raw)) return 'insect-amphibian';
    return raw || 'unknown';
  }

  function render(root, state) {
    if (!state.data) return;

    reconcileFilterAvailability(root, state);

    const rows = getFilteredRows(state);
    const isDesktop = window.matchMedia('(min-width: 621px)').matches;
    const canExpand = isDesktop && rows.length > 12;
    const displayedRows = canExpand && !state.expanded ? rows.slice(0, 12) : rows;
    state.visibleRows = displayedRows;
    const summaryRows = getSummaryRows(state);
    const isRolling = state.filter === 'rolling';
    const grid = root.querySelector('[data-fds-grid]');
    const status = root.querySelector('[data-fds-status]');
    const summary = root.querySelector('[data-fds-summary]');
    const intensityThresholds = getIntensityThresholds(rows);
    const totalDetections = summaryRows.reduce((sum, row) => sum + row.totalDetections, 0);

    if (status) {
      status.textContent = state.data.updatedAt ? `Updated ${formatUpdated(state.data.updatedAt)}` : 'Loaded';
    }

    if (summary) {
      summary.textContent = `${summaryRows.length} species · ${totalDetections.toLocaleString()} detections · ${state.data.updatedAt ? `Updated ${formatUpdated(state.data.updatedAt)}` : 'Recently updated'}`;
    }

    if (!rows.length) {
      grid.innerHTML = '<div class="wpc-full-day-soundscape__empty">No species match this view.</div>';
      updatePosition(root);
      return;
    }

    grid.innerHTML = `
      <div class="wpc-full-day-soundscape__axis">
        <div class="wpc-full-day-soundscape__corner">Species</div>
        <div class="wpc-full-day-soundscape__timeline-head">
          <div class="wpc-full-day-soundscape__periods" style="${solarGradientStyle(state.data.solar)}" aria-label="Time of day">
            <span class="is-night" style="grid-column:1 / span 5">Night</span>
            <span class="is-dawn" style="grid-column:6 / span 3">Dawn</span>
            <span class="is-morning" style="grid-column:9 / span 4">Morning</span>
            <span class="is-afternoon" style="grid-column:13 / span 5">Afternoon</span>
            <span class="is-evening" style="grid-column:18 / span 4">Evening</span>
            <span class="is-nightfall" style="grid-column:22 / span 3">Nightfall</span>
          </div>
          <div class="wpc-full-day-soundscape__hours">
            ${HOURS.map((hour, index) => `<div class="${hourClass(index, state)}" style="grid-column:${index + 1};" aria-label="${hourAriaLabel(hour, index, state)}">${formatHour(hour)}</div>`).join('')}
          </div>
        </div>
      </div>
      ${displayedRows.map(row => renderRow(row, intensityThresholds, isRolling, state.data.currentHour)).join('')}
    `;

    root.querySelectorAll('[data-fds-cell]').forEach(button => {
      button.addEventListener('click', () => showDetail(root, state, button));
    });

    const expand = root.querySelector('[data-fds-expand]');
    const expandButton = root.querySelector('[data-fds-expand-button]');
    if (expand && expandButton) {
      expand.hidden = !canExpand;
      expandButton.textContent = state.expanded ? 'Show fewer species' : 'Explore all species';
      expandButton.setAttribute('aria-expanded', String(state.expanded));
      expandButton.onclick = () => {
        state.expanded = !state.expanded;
        render(root, state);
      };
    }

    updatePosition(root);
  }

  function reconcileFilterAvailability(root, state) {
    const available = {
      all: true,
      rolling: state.data.rollingSpecies.length > 0,
      bird: state.rows.some(row => row.category === 'bird'),
      'insect-amphibian': state.rows.some(row => row.category === 'insect-amphibian'),
      'most-active': state.rows.length > 0,
      recent: state.rows.some(row => recentCounts(row, state.data.currentHour).some(Boolean))
    };

    if (!available[state.filter]) {
      state.filter = 'all';
    }

    root.querySelectorAll('[data-fds-filter]').forEach(button => {
      const key = button.dataset.fdsFilter || 'all';
      const active = key === state.filter;
      button.disabled = !available[key];
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function getFilteredRows(state) {
    let rows = state.filter === 'rolling' ? [...state.data.rollingSpecies] : [...state.rows];

    if (state.filter === 'rolling') {
      rows = rows.sort((a, b) => b.totalDetections - a.totalDetections);
    } else if (state.filter === 'bird') {
      rows = rows.filter(row => row.category === 'bird');
    } else if (state.filter === 'insect-amphibian') {
      rows = rows.filter(row => row.category === 'insect-amphibian');
    } else if (state.filter === 'most-active') {
      rows = rows.sort((a, b) => b.totalDetections - a.totalDetections).slice(0, 12);
    } else if (state.filter === 'recent') {
      rows = rows.filter(row => recentCounts(row, state.data.currentHour).some(Boolean));
    }

    if (state.search) {
      rows = rows.filter(row => `${row.commonName} ${row.scientificName}`.toLowerCase().includes(state.search));
    }

    if (state.filter !== 'rolling' && state.filter !== 'most-active') {
      rows.sort(compareFirstAppearance);
    }

    return rows;
  }

  function compareFirstAppearance(a, b) {
    const aAppearance = firstAppearance(a);
    const bAppearance = firstAppearance(b);

    if (aAppearance !== null && bAppearance !== null) {
      if (aAppearance.exactTimestamp !== null && bAppearance.exactTimestamp !== null && aAppearance.exactTimestamp !== bAppearance.exactTimestamp) {
        return aAppearance.exactTimestamp - bAppearance.exactTimestamp;
      }
      if (aAppearance.hour !== bAppearance.hour) return aAppearance.hour - bAppearance.hour;
    }
    if (aAppearance !== null && bAppearance === null) return -1;
    if (aAppearance === null && bAppearance !== null) return 1;

    return a.commonName.localeCompare(b.commonName, undefined, { sensitivity: 'base' });
  }

  function firstAppearance(row) {
    let earliestTimestamp = null;
    let exactTimestampHour = null;
    let earliestHour = null;

    row.hourlyCounts.forEach((count, hour) => {
      if (!count) return;
      if (earliestHour === null) earliestHour = hour;

      const timestamp = Date.parse(row.hourlyDetails[hour]?.firstHeardAt || '');
      if (Number.isFinite(timestamp) && (earliestTimestamp === null || timestamp < earliestTimestamp)) {
        earliestTimestamp = timestamp;
        exactTimestampHour = hour;
      }
    });

    if (earliestTimestamp !== null) return { exactTimestamp: earliestTimestamp, hour: exactTimestampHour };
    return earliestHour === null ? null : { exactTimestamp: null, hour: earliestHour };
  }

  function getSummaryRows(state) {
    return state.filter === 'rolling' ? state.data.rollingSpecies : state.rows;
  }

  function recentCounts(row, currentHour) {
    if (!Number.isFinite(currentHour)) return row.hourlyCounts.slice(-3);
    const start = Math.max(0, currentHour - 2);
    return row.hourlyCounts.slice(start, currentHour + 1);
  }

  function hourClass(index, state) {
    const classes = ['wpc-full-day-soundscape__hour'];
    if (isPriorDayHour(index, state.filter === 'rolling', state.data.currentHour)) classes.push('is-prior-day');
    return classes.join(' ');
  }

  function hourAriaLabel(hour, index, state) {
    const label = formatHour(hour);
    return isPriorDayHour(index, state.filter === 'rolling', state.data.currentHour) ? `${label}, previous day` : label;
  }

  function renderRow(row, intensityThresholds, isRolling, currentHour) {
    return `
      <div class="wpc-full-day-soundscape__row">
        <div class="wpc-full-day-soundscape__species">
          ${renderImage(row)}
          <span>
            <strong>${escapeHtml(row.commonName)}</strong>
            ${row.scientificName ? `<em>${escapeHtml(row.scientificName)}</em>` : ''}
          </span>
        </div>
        <div class="wpc-full-day-soundscape__cells">
          ${row.hourlyCounts.map((count, index) => renderCell(row, count, index, intensityThresholds, isRolling, currentHour)).join('')}
        </div>
      </div>
    `;
  }

  function renderImage(row) {
    if (!row.imageUrl) {
      return '<span class="wpc-full-day-soundscape__image-placeholder" aria-hidden="true">*</span>';
    }

    return `<img src="${escapeHtml(row.imageUrl)}" alt="${escapeHtml(row.commonName)}" loading="lazy">`;
  }

  function renderCell(row, count, index, intensityThresholds, isRolling, currentHour) {
    const normalizedCount = Math.max(0, Number(count || 0));
    const cellStyle = `grid-column:${index + 1};`;
    const cellClass = `wpc-full-day-soundscape__cell${isPriorDayHour(index, isRolling, currentHour) ? ' is-prior-day' : ''}`;

    if (!normalizedCount) {
      return `<div class="${cellClass} is-zero" style="${cellStyle}" data-hour="${index}"></div>`;
    }

    const detail = row.hourlyDetails[index] || {};
    const dayContext = isPriorDayHour(index, isRolling, currentHour) ? 'previous day, ' : '';
    const label = `${row.commonName}, ${dayContext}${formatHour(HOURS[index])} to ${formatHour(String((index + 1) % 24).padStart(2, '0'))}, ${normalizedCount} ${normalizedCount === 1 ? 'detection' : 'detections'}${detail.representativeAudioUrl ? ', representative audio available' : ''}.`;
    const intensity = intensityLevel(normalizedCount, intensityThresholds);

    return `
      <div class="${cellClass}" style="${cellStyle}" data-hour="${index}">
        <button
          type="button"
          class="wpc-full-day-soundscape__bubble is-intensity-${intensity}"
          data-fds-cell
          data-species="${escapeHtml(row.commonName)}"
          data-hour="${index}"
          aria-label="${escapeHtml(label)}"
          aria-pressed="false"
        >${escapeHtml(formatCount(normalizedCount))}</button>
      </div>
    `;
  }

  function isPriorDayHour(index, isRolling, currentHour) {
    return isRolling && Number.isFinite(currentHour) && index > currentHour;
  }

  function showDetail(root, state, button) {
    const detail = root.querySelector('[data-fds-detail]');
    if (!detail) return;

    const speciesName = button.dataset.species;
    const hourIndex = Number(button.dataset.hour);
    const row = (state.visibleRows || state.rows).find(item => item.commonName === speciesName);
    if (!row) return;

    root.querySelectorAll('[data-fds-cell]').forEach(cell => {
      const selected = cell === button;
      cell.classList.toggle('is-selected', selected);
      cell.setAttribute('aria-pressed', String(selected));
    });

    const dayContext = isPriorDayHour(hourIndex, state.filter === 'rolling', state.data.currentHour) ? 'Previous day - ' : '';
    const count = row.hourlyCounts[hourIndex] || 0;
    const item = row.hourlyDetails[hourIndex] || {};
    const audio = isPlayableUrl(item.representativeAudioUrl)
      ? `<a href="${escapeHtml(item.representativeAudioUrl)}">Open representative clip</a>`
      : '<span>No representative clip yet</span>';
    const firstLast = [item.firstHeardAt ? `First: ${formatTime(item.firstHeardAt)}` : '', item.lastHeardAt ? `Last: ${formatTime(item.lastHeardAt)}` : ''].filter(Boolean).join(' | ');

    detail.hidden = false;
    detail.innerHTML = `
      <div>
        <p class="wpc-full-day-soundscape__eyebrow">${dayContext}${formatHour(HOURS[hourIndex])} to ${formatHour(String((hourIndex + 1) % 24).padStart(2, '0'))}</p>
        <h3>${escapeHtml(row.commonName)}</h3>
        ${row.scientificName ? `<p><em>${escapeHtml(row.scientificName)}</em></p>` : ''}
        <p>${count.toLocaleString()} ${count === 1 ? 'detection' : 'detections'} in this hour.</p>
        ${firstLast ? `<p>${escapeHtml(firstLast)}</p>` : ''}
      </div>
      <div>${audio}</div>
    `;
    detail.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  function updatePosition(root) {
    const scroller = root.querySelector('[data-fds-scroller]');
    const position = root.querySelector('[data-fds-position]');
    if (!scroller || !position) return;

    const max = Math.max(1, scroller.scrollWidth - scroller.clientWidth);
    const width = Math.max(18, (scroller.clientWidth / Math.max(scroller.scrollWidth, 1)) * 100);
    const left = (scroller.scrollLeft / max) * (100 - width);
    position.style.width = `${Math.min(100, width)}%`;
    position.style.left = `${Math.max(0, left)}%`;
  }

  // Four levels use the 25th, 50th, and 75th percentiles of non-zero cells in the current filtered view.
  function getIntensityThresholds(rows) {
    const counts = rows.flatMap(row => row.hourlyCounts).filter(count => count > 0).sort((a, b) => a - b);
    if (!counts.length) return [1, 1, 1];
    return [0.25, 0.5, 0.75].map(percentile => counts[Math.min(counts.length - 1, Math.ceil(counts.length * percentile) - 1)]);
  }

  function intensityLevel(count, thresholds) {
    if (count <= thresholds[0]) return 'very-low';
    if (count <= thresholds[1]) return 'low';
    if (count <= thresholds[2]) return 'high';
    return 'highest';
  }

  function solarGradientStyle(solar) {
    if (!solar) {
      return 'background:linear-gradient(90deg,#172331 0%,#283f55 18%,#d29a62 28%,#f2d38a 52%,#c4875f 77%,#1d2c3a 100%);';
    }

    return 'background:linear-gradient(90deg,#172331 0%,#283f55 18%,#d29a62 28%,#f2d38a 52%,#c4875f 77%,#1d2c3a 100%);';
  }

  function formatHour(hour) {
    const value = Number(hour);
    if (!Number.isFinite(value)) return String(hour);
    if (value === 0) return '12 AM';
    if (value < 12) return `${value} AM`;
    if (value === 12) return '12 PM';
    return `${value - 12} PM`;
  }

  function formatCount(count) {
    if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k`;
    return String(count);
  }

  function formatUpdated(value) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return 'recently';
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function formatTime(value) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return String(value || '');
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  function dateFromTimestamp(value) {
    const date = value ? new Date(value) : new Date();
    if (!Number.isFinite(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  }

  function isPlayableUrl(value) {
    return /^https?:\/\//i.test(String(value || '').trim());
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function start() {
    document.querySelectorAll('[data-full-day-soundscape]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
})();
